import React, { useState, useRef, useEffect } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from '@nextui-org/react';
import { Tasks } from 'common/src/utils/types';
import { createTask, updateTask } from 'common/src/api/tasks';
import { toast } from 'react-toastify';
import UserInfoColumn, { UserInfoColumnRef } from './UserInfoColumn';
import TaskInfoColumn from './TaskInfoColumn';
import { updateAsker as updateAskerProfile } from 'common/src/api/askers'; // Add this import
import { cleanTaskData } from './utils';
import TaskStatusService from 'common/src/api/services/task_status_service'; // Add this import
import NotificationService from 'common/src/api/services/notification_service';
import useUserStore from '../../../../store/useUserStore';
import { supabase } from 'common/src/supabase';

interface ModalCreateTaskProps {
  isOpen: boolean;
  onClose: () => void;
  fetchTasks: () => Promise<void>;
  task?: Tasks;
  isEditMode: boolean; // New prop to distinguish between create and edit modes
}

const ModalCreateTask: React.FC<ModalCreateTaskProps> = ({
  isOpen,
  onClose,
  fetchTasks,
  task,
  isEditMode,
}) => {
  const [formData, setFormData] = useState<Partial<Tasks>>(task || {});
  const [isCreatingAddress, setIsCreatingAddress] = useState(false);
  const userInfoColumnRef = useRef<UserInfoColumnRef>(null);

  const { userProfile } = useUserStore();

  // Reset form data when opening the modal in create mode
  useEffect(() => {
    if (isOpen && !isEditMode) {
      setFormData({});
    } else if (isOpen && isEditMode && task) {
      setFormData(task);
    }
  }, [isOpen, isEditMode, task]);

  const handleCreateTask = async () => {
    try {
      setIsCreatingAddress(true);

      // First, create a new address if needed
      await userInfoColumnRef.current?.createNewAddressIfNeeded();

      // Update asker information
      if (formData.asker_id) {
        const askerUpdateData = {
          name: formData.name,
          last_name: formData.last_name,
          phone_number: formData.phone_number,
        };
        await updateAskerProfile(formData.asker_id, askerUpdateData);
      }

      // Fetch the latest form data after potential address creation
      const updatedFormData = userInfoColumnRef.current?.getUpdatedFormData() || formData;

      // Clean the form data before sending it to the API
      const cleanedFormData = cleanTaskData(updatedFormData as Tasks);

      // Ensure both category and category_id are included
      if (cleanedFormData.category_id) {
        cleanedFormData.category = cleanedFormData.category || formData.category;
      }

      // Add the source field for new tasks
      if (!task) {
        cleanedFormData.source = 'admin';
      }

      if (task) {
        try {
          const { status, ...payload } = cleanedFormData;

          // Update Task
          await updateTask(task.id, payload);
          toast.success('Task actualizada exitosamente');

          if (status && status !== task.status) {
            // Update Task status and send push notification
            const data = await TaskStatusService.changeTaskStatus(task.id.toString(), status);
            // Send email notification
            await NotificationService.notifyAsker(data);
          }
        } catch (error) {
          toast.error('Error al actualizar la task');
        }
      } else {
        // Create new task
        const taskResult = await createTask(cleanedFormData, userProfile.id);

        if (taskResult) {
          try {
            // Solo invocar automated offers si la task se creó exitosamente
            const { error: automatedOffersError } = await supabase.functions.invoke(
              'initiate-automated-offers',
              {
                body: { task_id: taskResult.id },
              }
            );

            if (automatedOffersError) {
              console.error('Error initiating automated offers:', automatedOffersError);
              // No lanzamos el error para no interrumpir el flujo principal
            }

            toast.success('Task creada exitosamente');
          } catch (error) {
            console.error('Error en el proceso de automated offers:', error);
            // La task ya se creó, así que seguimos con el flujo exitoso
            toast.success('Task creada exitosamente');
          }
        } else {
          toast.error('Error al crear la task');
        }
      }

      fetchTasks();
      onClose();
    } catch (error) {
      console.error('Error al guardar la task:', error);
      toast.error('Ocurrió un error inesperado al guardar la task.');
    } finally {
      setIsCreatingAddress(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl">
      <ModalContent>
        <ModalHeader className="flex justify-center">
          {isEditMode ? 'Editar Task' : 'Crear Nueva Task'}
        </ModalHeader>
        <ModalBody className="flex flex-row">
          <UserInfoColumn
            ref={userInfoColumnRef}
            formData={formData}
            setFormData={setFormData}
            task={task}
          />
          <div className="w-px bg-gray-200 mx-4"></div>
          <TaskInfoColumn formData={formData} setFormData={setFormData} isEditMode={isEditMode} />
        </ModalBody>
        <ModalFooter>
          <Button color="danger" variant="light" onPress={onClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleCreateTask}
            isLoading={isCreatingAddress}
            disabled={isCreatingAddress}
          >
            {isCreatingAddress ? 'Creating Address...' : task ? 'Guardar Cambios' : 'Crear Task'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalCreateTask;
